import React from "react";
import { LinkedinIcon } from "~/components/icons";
import metrics from "~/utils/metrics";
import { Button } from "@mindstonehq/ui";
import * as Sentry from "@sentry/react";
import { ShareIcon } from "@heroicons/react/24/outline";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface LinkedInShareButtonProps {
  id: string;
  name: string;
  awardedOn?: string;
  shareURL?: string;
  grantId?: string;
  variant?: "default" | "link" | "outline" | "secondary";
  size?: "sm" | "lg" | "default" | "icon";
}

export default function LinkedInShareButton({
  id,
  name,
  awardedOn,
  shareURL,
  grantId,
  size = "lg",
  variant = "default",
}: LinkedInShareButtonProps) {
  const { t } = useLingui();
  const handleLinkedin = () => {
    if (!awardedOn || !shareURL || shareURL === "") {
      Sentry.captureException(new Error("Cannot share badge to LinkedIn"), {
        tags: {
          name: name,
          grantId: grantId,
        },
        extra: {
          shareURL: shareURL,
          awardedOn: awardedOn,
        },
      });
      return;
    }

    metrics.track("did_tap_share_badge", {
      type: "linkedin",
      url: shareURL,
    });

    const mindstoneLinkedInOrgId = "70887821";

    const awardDateObj = new Date(awardedOn);
    const issueYear = awardDateObj.getUTCFullYear();
    const issueMonth = awardDateObj.getUTCMonth() + 1; // getUTCMonth() returns 0-11

    // Calculate expiration date (one year later)
    const expirationDate = new Date(awardDateObj);
    expirationDate.setUTCFullYear(expirationDate.getUTCFullYear() + 1);
    const expirationYear = expirationDate.getUTCFullYear();
    const expirationMonth = expirationDate.getUTCMonth() + 1;

    const linkedinParams = new URLSearchParams({
      startTask: id,
      name,
      organizationId: mindstoneLinkedInOrgId,
      issueYear: issueYear.toString(),
      issueMonth: issueMonth.toString(),
      expirationYear: expirationYear.toString(),
      expirationMonth: expirationMonth.toString(),
      certUrl: shareURL,
      certId: grantId ?? "",
    });

    const linkedinUrl = `https://www.linkedin.com/profile/add?${linkedinParams.toString()}`;

    // eslint-disable-next-line lingui/no-unlocalized-strings -- window.open target
    window.open(linkedinUrl, "_blank");
  };

  return (
    <Button
      onClick={handleLinkedin}
      variant={variant}
      size={size}
      aria-label={size === "icon" ? t(msg`Share certificate on LinkedIn`) : undefined}
    >
      {size === "icon" ? (
        <ShareIcon className="h-4 w-4" />
      ) : (
        <LinkedinIcon className="mb-1" />
      )}
      {size !== "icon" && (
        <span>{size === "sm" ? t(msg`Share`) : t(msg`Share certificate`)}</span>
      )}
    </Button>
  );
}
